<template>
  <div v-if="headhunterApiAccess === null || !headhunterApiAccess.search">
    <template v-if="isHeadhunterLoading">
      <vpreloader width="2" size="64" />
    </template>

    <template v-else>
      Проверьте авторизованы ли вы в настройках
      <router-link tag="a" :to="{ name: 'settings-integrations' }">интеграции</router-link>
      Headhunter
      <br />
      <br />
      У вашей компании также должен быть опаченный доступ к API Headhunter
    </template>
  </div>

  <div v-else class="headhunter-autosearch">
    <div class="headhunter-autosearch__title">Настройки</div>

    <div class="headhunter-autosearch__block">
      <div class="headhunter-autosearch__field">
        <div class="headhunter-autosearch__field-text">Включить функцию автопоиска</div>

        <div class="headhunter-autosearch__field-inputs">
          <trigger v-model="formCommon.active" />
        </div>
      </div>

      <div class="headhunter-autosearch__field">
        <div class="headhunter-autosearch__field-text">
          График работы

          <img
            v-tooltip="{
              content: 'Автопоиск будет искать и приглашать новых кандидатов раз в час согласно указанному графику',
              placement: 'top-center',
              classes: 'tight tight-330'
            }"
            class="vmodule_icon-lock"
            src="/static/images/elements/vmodule-icon-info.svg"
          />
        </div>

        <div class="headhunter-autosearch__field-inputs">
          <vinput
            v-model="formCommon.start_search_time"
            :validate="'date_format:HH:mm'"
            :disabled="isHeadhunterLoading"
            class="headhunter-autosearch__group-input"
            placeholder="От"
            border
          />
          <vinput
            v-model="formCommon.end_search_time"
            :validate="'date_format:HH:mm'"
            :disabled="isHeadhunterLoading"
            class="headhunter-autosearch__group-input"
            placeholder="До"
            border
          />
        </div>
      </div>

      <div class="headhunter-autosearch__field">
        <div class="headhunter-autosearch__field-text">Часовой пояс</div>

        <div class="headhunter-autosearch__field-inputs headhunter-autosearch__group-multiply">
          <multiselect
            ref="multiselect"
            v-model="formCommon.timezone"
            v-validate="'required'"
            :options="headhunterTimezones"
            :show-labels="false"
            :internal-search="false"
            :loading="isHeadhunterLoading"
            track-by="id"
            label="name"
            allow-empty
            searchable
          />
        </div>
      </div>
    </div>

    <div class="headhunter-autosearch__title">Лимиты</div>

    <div class="headhunter-autosearch__block">
      <div class="headhunter-autosearch__field">
        <div class="headhunter-autosearch__field-text">Включить лимиты</div>

        <div class="headhunter-autosearch__field-inputs">
          <trigger v-model="formCommon.limits_active" :disabled="isHeadhunterLoading" />
        </div>
      </div>

      <div v-show="formCommon.limits_active" class="headhunter-autosearch__field">
        Приглашать
        <div class="headhunter-autosearch__field-input">
          <vinput
            v-model="formCommon.daily_limit"
            :validate="'required|numeric'"
            :disabled="isHeadhunterLoading"
            border
          />
        </div>
        {{ declOfNum(formCommon.daily_limit, ['кандидата', 'кандидатов', 'кандидатов']) }}
        в сутки, но не больше
        <div class="headhunter-autosearch__field-input">
          <vinput
            v-model="formCommon.total_limit"
            :validate="'required|numeric'"
            :disabled="isHeadhunterLoading"
            border
          />
        </div>
        за весь период
      </div>

      <div v-show="formCommon.limits_active" class="headhunter-autosearch__field headhunter-autosearch--grey">
        Приглашено сегодня: {{ formCommon.daily_usage }}, за весь срок: {{ formCommon.total_usage }} |&nbsp;
        <span
          class="headhunter-autosearch__grey-link"
          :class="{ disabled: isHeadhunterLoading }"
          @click="resetStatistic()"
        >
          Сбросить лимиты
        </span>
      </div>
    </div>

    <div class="headhunter-autosearch__title">Фильтры</div>

    <div class="headhunter-autosearch__block">
      <div class="headhunter-autosearch__fields">
        <div
          v-for="group in headhunterSearchFieldsGrouped"
          :key="group.id"
          class="headhunter-autosearch__group-wrapper"
        >
          <div class="headhunter-autosearch__group-name">
            {{ $tr(`headhunter.autosearch.${group.name}`) }}
          </div>

          <div class="headhunter-autosearch__group">
            <div v-for="field in group._embedded.fields" :key="field.id" class="headhunter-autosearch__group-field">
              <template v-if="field.type === 'checkbox'">
                <trigger v-model="formFields[field.id]" :disabled="isHeadhunterLoading" />
              </template>

              <template v-if="field.type === 'list'">
                <multiselect
                  ref="multiselect"
                  v-model="formFields[field.id]"
                  class="headhunter-autosearch__group-select"
                  :class="{ 'headhunter-autosearch__group-multiply': field.multiple }"
                  :options="field.dictionary"
                  :show-labels="false"
                  :internal-search="false"
                  :multiple="field.multiple"
                  :loading="isHeadhunterLoading"
                  track-by="id"
                  label="name"
                  searchable
                  allow-empty
                />
              </template>

              <template
                v-if="field.type === 'int' && (field.search_code === 'age_from' || field.search_code === 'age_to')"
              >
                <vinput
                  v-model="formFields[field.id]"
                  :validate="'numeric'"
                  :disabled="isHeadhunterLoading"
                  class="headhunter-autosearch__group-input"
                  border
                />
              </template>

              <template
                v-if="field.type === 'int' && field.search_code !== 'age_from' && field.search_code !== 'age_to'"
              >
                <vinput
                  v-model="formFields[field.id]"
                  :validate="'numeric'"
                  :disabled="isHeadhunterLoading"
                  class="headhunter-autosearch__group-input"
                  border
                />
              </template>

              <template v-if="field.type === 'suggest'">
                <div class="headhunter-autosearch__group-suggest">
                  <vcv-suggest
                    :is-disabled="isHeadhunterLoading"
                    :suggest-array="suggests[field.suggest]"
                    :selected-items="formFields[field.id]"
                    @suggest="makeSuggest(field.suggest, $event)"
                    @select-suggest="addSuggest(field.id, $event)"
                    @remove-badge="removeBadge(field.id, $event)"
                  />
                </div>
              </template>

              <template v-if="field.type === 'string'">
                <vinput
                  v-model="formFields[field.id]"
                  :disabled="isHeadhunterLoading"
                  class="headhunter-autosearch__group-input headhunter-autosearch__group-multiply"
                  border
                />
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="headhunter-autosearch__block headhunter-autosearch__block-flex">
      <vbutton text="Сохранить" :loading="isHeadhunterLoading" @click="saveAutosearchOptions()" />

      <vbutton
        v-tooltip="{
          content: 'Данная функция не изменяет лимит по API и является бесплатной.',
          placement: 'top-center',
          classes: 'tight tight-330'
        }"
        text="Просчитать количество кандидатов"
        color="grey"
        :disabled="isTestSearchDisabled"
        :loading="isHeadhunterLoading"
        @click="makeTestSearch()"
      />

      <img
        v-tooltip="{
          content:
            'После сохранения критериев поиска вы можете рассчитать примерное количество кандидатов которые будут приглашены',
          placement: 'top-center',
          classes: 'tight tight-330'
        }"
        class="vmodule_icon-lock"
        src="/static/images/elements/vmodule-icon-info.svg"
      />
    </div>

    <div v-if="headhunterTestSearch !== null" class="headhunter-autosearch__block">
      По вашим критериям поиска на данный момент будет найдено примерно {{ headhunterTestSearch }}
      {{ declOfNum(headhunterTestSearch, ['кандидата', 'кандидатов', 'кандидатов']) }}
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
import { declOfNum } from '@/_helpers/utils.js';
import Trigger from '@/components/shared/Trigger';
import VcvSuggest from '@/components_new/VcvSuggest';

export default {
  components: {
    Trigger,
    VcvSuggest
  },

  props: {
    vcvVacancyId: {
      type: [String, Number],
      default: ''
    }
  },

  data() {
    return {
      formCommon: {
        vcv_vacancy_id: this.vcvVacancyId,
        start_search_time: '00:00',
        end_search_time: '00:00',
        daily_usage: 0,
        total_usage: 0,
        daily_limit: 1,
        total_limit: 1,
        limits_active: false,
        active: false,
        timezone: ''
      },

      formFields: {},
      suggests: {},
      isTestSearchDisabled: true
    };
  },

  computed: {
    ...mapState('integrationsHeadhunter', ['headhunterApiAccess']),

    ...mapState('integrationsHeadhunterAutosearch', [
      'headhunterSearchFieldsGrouped',
      'headhunterTimezones',
      'isHeadhunterLoading',
      'headhunterTestSearch'
    ]),

    ...mapGetters('integrationsHeadhunterAutosearch', [
      'getTimezoneByKey',
      'getHeadhunterAutosearchFieldProperties',
      'getDictionaryObjectForSelect'
    ]),

    formFieldsForPatch() {
      const newFormFields = {
        _embedded: {
          values: []
        }
      };

      for (let key in this.formFields) {
        if (this.formFields[key]) {
          // Если значение является массивом, то создаем { field_id: key, value: x } для каждого из вложенных значений
          if (Array.isArray(this.formFields[key])) {
            for (var i = 0; i < this.formFields[key].length; i++) {
              // Не спрашивайте зачем это... Это из-за того что значения приходят из hh.ru и возникает путаница что такое value (надо получается id -> value, name -> title)
              newFormFields._embedded.values.push({
                field_id: key,
                value:
                  this.formFields[key][i].external_id !== undefined
                    ? this.formFields[key][i].external_id
                    : this.formFields[key][i].value !== undefined
                    ? this.formFields[key][i].value
                    : this.formFields[key][i].id,
                title: this.formFields[key][i].name
              });
            }
          } else if (this.formFields[key] === true || this.formFields[key] === false) {
            // для чекбоксов конвертируем 1/0 в true/false
            newFormFields._embedded.values.push({
              field_id: key,
              value: this.formFields[key] === true ? true : false
            });
          } else if (this.formFields[key] !== null && typeof this.formFields[key] === 'object') {
            // Если значение является объектом (выбрано одно из мультиселекта)
            newFormFields._embedded.values.push({
              field_id: key,
              value: this.formFields[key].external_id
            });
          } else {
            newFormFields._embedded.values.push({ field_id: key, value: this.formFields[key] });
          }
        }
      }

      return newFormFields;
    }
  },

  async created() {
    if (this.headhunterApiAccess === null) {
      await this.fetchApiAccess();
    }

    if (this.vcvVacancyId) {
      await this.fetchTimeZones();
      await this.fetchFormFields().then(response => {
        this.formFields = response;
      });
      await this.checkAttachedVacancy(this.vcvVacancyId).then(response => {
        if (response !== null) {
          this.formCommon.active = response.active;
          this.formCommon.daily_limit = response.daily_limit;
          this.formCommon.daily_usage = response.daily_usage;
          this.formCommon.end_search_time = response.end_search_time;
          this.formCommon.limits_active = response.limits_active;
          this.formCommon.start_search_time = response.start_search_time;
          this.formCommon.timezone = this.getTimezoneByKey(response.timezone);
          this.formCommon.total_limit = response.total_limit;
          this.formCommon.total_usage = response.total_usage;

          // Set fields values
          const fieldsValues = response._embedded.values;

          if (fieldsValues.length > 0) {
            for (let i = 0; i < fieldsValues.length; i++) {
              const fieldId = fieldsValues[i].field_id;
              const fieldsProperties = this.getHeadhunterAutosearchFieldProperties(fieldId);

              if (fieldsProperties.type === 'checkbox') {
                this.formFields[fieldId] = fieldsValues[i].value === '1' ? true : false;
              } else if (fieldsProperties.type === 'suggest') {
                let suggestItem = fieldsValues[i];
                suggestItem.name = fieldsValues[i].title;

                if (this.formFields[fieldId].length > 0) {
                  this.formFields[fieldId].push(suggestItem);
                } else {
                  this.formFields[fieldId] = [suggestItem];
                }
              } else if (fieldsProperties.type === 'list') {
                if (this.formFields[fieldId] === null) {
                  this.formFields[fieldId] = [this.getDictionaryObjectForSelect(fieldId, fieldsValues[i].value)];
                } else {
                  this.formFields[fieldId].push(this.getDictionaryObjectForSelect(fieldId, fieldsValues[i].value));
                }
              } else {
                this.formFields[fieldId] = fieldsValues[i].value;
              }
            }
          }
        } else {
          // set default first timezone from fetched list
          this.formCommon.timezone = this.headhunterTimezones[0];
        }
      });

      // clear testsearch data
      this.SET_HEADHUNTER_TEST_SEARCH(null);
    }
  },

  methods: {
    ...mapMutations('integrationsHeadhunterAutosearch', ['SET_HEADHUNTER_TEST_SEARCH']),

    ...mapActions('integrationsHeadhunter', ['fetchApiAccess']),

    ...mapActions('integrationsHeadhunterAutosearch', [
      'checkAttachedVacancy',
      'connectVacancySearchToVcv',
      'fetchTimeZones',
      'fetchFormFields',
      'saveAutosearch',
      'resetAutosearchStatistic',
      'fetchSuggest',
      'fetchTestSearch'
    ]),

    async saveAutosearchOptions() {
      const params = {
        ...this.formCommon,
        ...this.formFieldsForPatch,
        timezone: this.formCommon.timezone.id
      };

      await this.saveAutosearch(params).then(() => {
        // enable test search button after save
        this.isTestSearchDisabled = false;
      });
    },

    async resetStatistic() {
      await this.resetAutosearchStatistic().then(() => {
        this.formCommon.daily_usage = 0;
        this.formCommon.total_usage = 0;
      });
    },

    async makeSuggest(suggestCode, suggestValue) {
      if (suggestValue.length > 1) {
        await this.fetchSuggest({ code: suggestCode, value: suggestValue }).then(response => {
          this.$set(this.suggests, suggestCode, response);
        });
      } else {
        this.suggests[suggestCode] = [];
      }
    },

    addSuggest(fieldId, value) {
      if (this.formFields[fieldId] !== null && this.formFields[fieldId].length > 0) {
        const index = this.formFields[fieldId].findIndex(x => x.id === value.id);

        if (index < 0) {
          this.formFields[fieldId].push(value);
        }
      } else {
        this.formFields[fieldId] = [value];
      }
    },

    removeBadge(fieldId, itemId) {
      const index = this.formFields[fieldId].findIndex(x => x.id === itemId);

      if (index >= 0) {
        this.formFields[fieldId].splice(index, 1);
      }
    },

    declOfNum(number, wordsArray) {
      return declOfNum(number, wordsArray);
    },

    async makeTestSearch() {
      // Save
      await this.saveAutosearchOptions();
      // Clear test search
      this.SET_HEADHUNTER_TEST_SEARCH(null);
      // Fetch Test Search - returns count of found resumes at this moment
      await this.fetchTestSearch();
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.headhunter-autosearch {
  display: flex;
  flex-direction: column;
  margin-bottom: 100px;

  &--grey {
    color: #9faab6;
  }

  &__grey-link {
    text-decoration: underline;
    cursor: pointer;
    transition: color 0.3s;

    &:hover {
      color: darken(#9faab6, 10%);
      transition: color 0.3s;
    }

    &.disabled {
      pointer-events: none;
      opacity: 0.7;
      cursor: not-allowed;
    }
  }

  &__block {
    margin-bottom: 30px;
  }

  &__block-flex {
    display: flex;
    align-items: center;

    > img {
      margin-left: 20px;
    }
  }

  &__field {
    margin-bottom: 15px;
    height: 40px;
    display: flex;
    align-items: center;
  }

  &__field-text {
    width: 250px;
    font-size: 15px;
    padding-right: 10px;
    display: flex;
    align-items: center;

    > img {
      margin-left: 10px;
    }
  }

  &__field-input {
    width: 60px;
    margin: 0 10px;
  }

  &__field-inputs {
    display: flex;
    align-items: center;
    padding: 0 12px;
  }

  &__input-small {
    width: 62px;
  }

  &__title {
    font-weight: 500;
    font-size: 22px;
    color: #233346;
    margin-bottom: 27px;
  }

  &__group-wrapper {
    display: flex;
  }

  &__group-name {
    width: 250px;
    line-height: 1.5;
    padding: 16px 12px;
    background: rgba(244, 245, 247, 0.65);
    border-right: 1px solid $green;
  }

  &__group {
    display: flex;
    padding: 0 12px;
    align-items: center;
  }

  &__group-input {
    width: 95px;
    margin-right: 10px;
  }

  &__group-select {
    width: 200px;
  }

  &__group-multiply,
  &__group-suggest {
    max-width: 410px;
    width: 410px;
  }
}
</style>
